import { AchException } from '@treasury/domain/arp';
import { PayOrReturnChoice } from '@treasury/domain/channel/mappings/ach';
import { TmContainer } from '@treasury/presentation';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ConfirmationType } from '../../../components/confirmation-container';
import '../partials/ach-exception-detail';
import '../partials/ach-exceptions-activity-table';
import '../partials/ach-exceptions-table';
import { AchExceptionActivityVm } from '../view-models/ach-exception-activity';

export enum AchExceptionsConfig {
    List = 'list',
    Review = 'review',
    Confirm = 'confirm',
}

export const tagName = 'ach-exception-workflow-container';
@customElement(tagName)
export class AchExceptionWorkflowContainer extends TmContainer {
    @property({ type: String })
    public step = AchExceptionsConfig.List;

    @property({ type: Array })
    public achExceptions: AchException[] = [];

    @property({ type: Array })
    public accounts: string[] = [];

    @property()
    public afterCutoff = false;

    @property({ type: String })
    public activeTab: 'ach-exceptions' | 'ach-exceptions-activity' = 'ach-exceptions';

    @property({ type: Array })
    public secCodes: string[] = [];

    @property({ type: Boolean })
    public primaryActionLoading = false;

    @state()
    private decisionedAchExceptions: AchException[] = [];

    @state()
    private achExceptionActivity: AchExceptionActivityVm[] = [];

    public render() {
        switch (this.step) {
            case AchExceptionsConfig.List:
                if (this.activeTab === 'ach-exceptions') {
                    return html`<ach-exceptions-table
                            .loading=${this.loading}
                            ?readonly=${this.afterCutoff}
                            .achExceptions=${this.achExceptions}
                            .accounts=${this.accounts}
                            .secCodes=${this.secCodes}
                            @decision=${() => {
                                this.countDecisionedExceptions();
                            }}
                            @selected=${(event: CustomEvent<AchException>) => {
                                this.dispatchEvent(
                                    new CustomEvent('selected', { detail: event.detail })
                                );
                            }}
                            @decisionAll=${(event: CustomEvent<PayOrReturnChoice>) => {
                                this.dispatchEvent(
                                    new CustomEvent('decisionAll', { detail: event.detail })
                                );
                                this.countDecisionedExceptions();
                            }}
                            @clearAll=${() => {
                                this.dispatchEvent(
                                    new CustomEvent('decisionAll', {
                                        detail: PayOrReturnChoice.Unselected,
                                    })
                                );
                                this.countDecisionedExceptions();
                            }}
                        >
                        </ach-exceptions-table>
                        <div class="flex w-full fixed bottom-0 paper">
                            <tm-footer
                                .showTopShadow=${false}
                                .buttonConfig=${[
                                    {
                                        text: `Review${
                                            this.decisionedAchExceptions?.length
                                                ? ` (${this.decisionedAchExceptions.length})`
                                                : ''
                                        }`,
                                        onClick: () => {
                                            this.dispatchEvent(new CustomEvent('review'));
                                        },
                                        disabled: !this.decisionedAchExceptions.length,
                                    },
                                ]}
                            ></tm-footer>
                        </div>`;
                }
                return html`<ach-exceptions-activity-table
                    .loading=${this.loading}
                    .readonly=${this.afterCutoff}
                    .achExceptions=${this.achExceptionActivity}
                    .accounts=${this.accounts}
                    .secCodes=${this.secCodes}
                ></ach-exceptions-activity-table>`;
            case AchExceptionsConfig.Review:
                return html`
                    <ach-exceptions-table
                        .loading=${this.loading}
                        .achExceptions=${this.achExceptions?.filter(
                            ae => ae.decisionChoice !== PayOrReturnChoice.Unselected
                        )}
                        @selected=${(event: CustomEvent<AchException>) => {
                            this.dispatchEvent(
                                new CustomEvent('selected', { detail: event.detail })
                            );
                        }}
                        .actionName=${'Confirm'}
                        .actionLoading=${this.primaryActionLoading}
                        .readonly=${true}
                        .hasFooterMessage=${true}
                        @action=${(event: CustomEvent<AchException[]>) => {
                            this.dispatchEvent(new CustomEvent('action', { detail: event.detail }));
                        }}
                    >
                    </ach-exceptions-table>
                    <tm-footer
                        .buttonConfig=${[
                            {
                                text: `Decision${
                                    this.decisionedAchExceptions?.length
                                        ? ` (${this.decisionedAchExceptions.length})`
                                        : ''
                                }`,
                                onClick: () => {
                                    this.dispatchEvent(
                                        new CustomEvent('confirm', {
                                            detail: this.decisionedAchExceptions,
                                        })
                                    );
                                },
                                disabled: this.loading,
                            },
                        ]}
                        ><div class="background-white text-center w-full bg-primary p-3">
                            <div class="bg-page rounded p-2">
                                <span class="text-sm text-gray-600"
                                    >ACH items can only be decisioned one time. Select
                                    'Decision' to continue.</span
                                >
                            </div>
                        </div></tm-footer
                    >
                </div>`;

            case AchExceptionsConfig.Confirm:
                return html`<confirmation-container
                        class="pt-10"
                        .confirmationType=${ConfirmationType.Success}
                        .headingText=${'Decision Submitted'}
                        .subheadingText=${'Your exceptions have been successfully decisioned.'}
                        iconSize="small"
                    >
                        <ach-exceptions-table
                            .loading=${this.loading}
                            .hideTotals=${true}
                            .achExceptions=${this.achExceptions?.filter(
                                ae => ae.decisionChoice !== PayOrReturnChoice.Unselected
                            )}
                            .actionLoading=${this.primaryActionLoading}
                            readonly
                        >
                        </ach-exceptions-table>
                    </confirmation-container>
                    <div class="flex w-full fixed bottom-0 paper">
                        <tm-footer
                            .buttonConfig=${[
                                {
                                    text: 'View ACH exceptions',
                                    onClick: () => {
                                        this.step = AchExceptionsConfig.List;
                                        this.dispatchEvent(
                                            new CustomEvent('list', { detail: { reload: true } })
                                        );
                                    },
                                    importance: 'secondary',
                                },
                                {
                                    text: 'View check exceptions',
                                    onClick: () => {
                                        this.step = AchExceptionsConfig.List;
                                        this.dispatchEvent(new CustomEvent('check-exceptions'));
                                    },
                                    importance: 'secondary',
                                },
                            ]}
                        ></tm-footer>
                    </div>`;
            default:
                return nothing;
        }
    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>): void {
        if (changedProperties.has('step') && this.step === AchExceptionsConfig.Confirm) {
            this.decisionedAchExceptions = [];
        }
    }

    private countDecisionedExceptions() {
        this.decisionedAchExceptions = this.afterCutoff
            ? []
            : this.achExceptions.filter(ae => ae.decisionChoice !== PayOrReturnChoice.Unselected);
    }

    public static get styles() {
        return [
            css`
                :host {
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AchExceptionWorkflowContainer;
    }
}
